import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import toast from 'react-hot-toast';

import { AffiliateTaxInfoDetail, RevokeAffiliateTaxInfoDto } from '../domain/types';
import { affiliateManagementService } from '../infra/affiliateManagementService';

export const affiliateTaxInfoQueryKeys = {
  detail: (projectId: string, userIdentifier: string) => ['affiliate-tax-info', projectId, userIdentifier] as const,
};

/**
 * Admin-only hook: fetches a single affiliate's tax info with masked identifiers.
 * Returns `null` when no tax info has been submitted yet.
 */
export function useAffiliateTaxInfo(projectId: string | undefined, userIdentifier: string | undefined) {
  return useQuery<AffiliateTaxInfoDetail | null>({
    queryKey: affiliateTaxInfoQueryKeys.detail(projectId ?? '', userIdentifier ?? ''),
    queryFn: () => affiliateManagementService.getAffiliateTaxInfo(projectId as string, userIdentifier as string),
    enabled: !!projectId && !!userIdentifier,
    refetchOnWindowFocus: false,
    staleTime: 60_000,
  });
}

/**
 * Revokes an affiliate's tax info. On success we invalidate the affiliates list so the
 * `tax_status` column reflects the new `revoked` state, and we write the revoked payload
 * into the per-affiliate cache so the detail modal updates immediately.
 *
 * `projectId`/`userIdentifier` are captured via refs so that a project switch mid-revoke
 * doesn't update the wrong cache.
 */
export function useRevokeAffiliateTaxInfo(projectId: string | undefined, userIdentifier: string | undefined) {
  const queryClient = useQueryClient();
  const projectIdRef = useRef(projectId);
  const userIdentifierRef = useRef(userIdentifier);
  useEffect(() => {
    projectIdRef.current = projectId;
  }, [projectId]);
  useEffect(() => {
    userIdentifierRef.current = userIdentifier;
  }, [userIdentifier]);

  return useMutation<AffiliateTaxInfoDetail, Error, RevokeAffiliateTaxInfoDto>({
    mutationFn: (body) => {
      const pid = projectIdRef.current as string;
      const uid = userIdentifierRef.current as string;
      return affiliateManagementService.revokeAffiliateTaxInfo(pid, uid, body);
    },
    onSuccess: (data) => {
      const pid = projectIdRef.current;
      const uid = userIdentifierRef.current;
      if (pid && uid) {
        queryClient.setQueryData(affiliateTaxInfoQueryKeys.detail(pid, uid), data);
      }
      if (pid) {
        queryClient.invalidateQueries({ queryKey: ['affiliate-management', 'list', pid] });
      }
      toast.success('Tax info revoked.');
    },
    onError: (err) => {
      toast.error(err.message || 'Failed to revoke tax info.');
    },
  });
}
